import { Unit } from "types/landlord/address";
import { PhotosForm } from "shared/app/PhotosForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { MediaItem } from "shared/types/listing";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { PhotoWithStatus } from "shared/app/PhotosForm/types";

interface PhotosSectionProps {
  unit: Unit;
}

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

const getSmallMedia = (media: MediaItem) => {
  return media.attachments?.find((a) => a.resolution === "small");
};

const getFullMedia = (media: MediaItem) => {
  return media.attachments?.find((a) => a.resolution === "full");
};

const transformMedia = (media: Unit["media"]): PhotoWithStatus[] => {
  return (
    media?.map((media) => {
      const smallMedia = getSmallMedia(media);
      const fullMedia = getFullMedia(media);
      return {
        id: media.id.toString(),
        status: "uploaded",
        resolutions: {
          small: {
            file: new File([], ""),
            uploadedUrl: smallMedia?.url || ""
          },
          full: {
            file: new File([], ""),
            uploadedUrl: fullMedia?.url || ""
          }
        },
        previewUrl: smallMedia?.url || ""
      };
    }) || []
  );
};

export const PhotosSection = ({ unit }: PhotosSectionProps) => {
  const queryClient = useQueryClient();

  const { mutate: uploadMedia, isPending } = useMutation({
    mutationFn: (
      media: {
        content_type: string;
        resolutions: {
          asset_id: string;
          resolution: string;
        }[];
      }[]
    ) => {
      return addressApi.editUnit(unit.id, {
        media_session_key: sessionKey,
        media: media
      });
    },
    onError: (error) => {
      toast.error(`Failed to upload photos: ${getErrorMessage(error)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["unit", String(unit.property.id), String(unit.id)]
      });
    }
  });

  return (
    <PhotosForm
      onChange={(media) => uploadMedia(media)}
      isUploading={isPending}
      sessionKey={sessionKey}
      onDeleteSuccess={() =>
        queryClient.invalidateQueries({
          queryKey: ["unit", String(unit.property.id), String(unit.id)]
        })
      }
      onMakeDefaultSuccess={() =>
        queryClient.invalidateQueries({
          queryKey: ["unit", String(unit.property.id), String(unit.id)]
        })
      }
      uploadedPhotos={transformMedia(unit.media)}
    />
  );
};
